import {
  ActionContext,
  Store
} from 'vuex'

export enum Setters {
  SetContent = 'setContent',
  SetLoading = 'setLoading',
  SetException = 'setException'
}

export enum Properties {
  Loading = 'loading',
  Exception = 'exception',
  Content = 'content',

  FetchContent = 'fetchContent',
}

export interface State<ContentType> {
  [Properties.Content]: ContentType | null;
  [Properties.Loading]: boolean;
  [Properties.Exception]: boolean;
}

export type ContentPageStore<ContentType> = Store<State<ContentType>>

interface Accessors<ContentType, FetcherPayload> {
  fetchContent: (store: ContentPageStore<ContentType>, payload: FetcherPayload) => Promise<void>;
  getLoading: (store: ContentPageStore<ContentType>) => boolean;
  getException: (store: ContentPageStore<ContentType>) => boolean;
  getContent: (store: ContentPageStore<ContentType>) => ContentType | null;
}

export const accessorFactory = <ContentType, FetcherPayload = void | string>(namespace: string): Accessors<ContentType, FetcherPayload> => {
  return {
    fetchContent: (store, payload) => store.dispatch(`${namespace}/${Properties.FetchContent}`, payload),
    getLoading: store => store.getters[`${namespace}/${Properties.Loading}`],
    getException: store => store.getters[`${namespace}/${Properties.Exception}`],
    getContent: store => store.getters[`${namespace}/${Properties.Content}`]
  }
}

type FetcherFn<Result, Payload> = (params: Payload) => Promise<Result>;
type TransformerFn<ContentType> = (content: any) => ContentType; // eslint-disable-line

export const storeFactory = <ContentType, FetcherPayload = void>(
  contentFetcher: FetcherFn<ContentType, FetcherPayload>,
  transform: TransformerFn<ContentType> = a => a
) => {
  return {
    namespaced: true,
    state: {
      [Properties.Content]: null,
      [Properties.Loading]: true,
      [Properties.Exception]: false
    },
    getters: {
      [Properties.Content]: (state: State<ContentType>) => state.content,
      [Properties.Exception]: (state: State<ContentType>) => state.exception,
      [Properties.Loading]: (state: State<ContentType>) => state.loading
    },
    mutations: {
      [Setters.SetLoading]: (state: State<ContentType>, loading: boolean) => {
        state.loading = loading
      },
      [Setters.SetException]: (state: State<ContentType>, exception: boolean) => {
        state.exception = exception
      },
      [Setters.SetContent]: (
        state: State<ContentType>, content: ContentType
      ) => {
        state.content = content
      }
    },
    actions: {
      [Properties.FetchContent]: async (
        { commit }: ActionContext<State<ContentType>, {}>,
        fetcherPayload: FetcherPayload
      ): Promise<void> => {
        try {
          commit(Setters.SetException, false)
          commit(Setters.SetLoading, true)
          const content = await contentFetcher(fetcherPayload)
          commit(Setters.SetContent, transform(content))
        } catch (err) {
          commit(Setters.SetException, true)
        } finally {
          commit(Setters.SetLoading, false)
        }
      }
    }
  }
}
